// TODO: cleanup with cp_annotation_createproject_agent ff

/**
 * Human quotas for a project with the annotation agent, computed the way the API's
 * `annotation_agent/allocation.py` computes them, so step 6 can show the counts the project
 * will be created with. The two are kept in step by the allocation vectors table that both
 * test suites copy (TDD VVA-3909 section 5.7); this library cannot import from the API app.
 */

/** A whole number from 0 to 100 without leading zeros. */
const SHARE_PERCENTAGE_PATTERN = /^(0|[1-9][0-9]?|100)$/;

export const isValidSharePercentage = (value: string): boolean =>
  SHARE_PERCENTAGE_PATTERN.test(value);

/** Default mode: an even split, with the remainder going to the last human. */
export const defaultQuotas = (encounterCount: number, humanCount: number): number[] => {
  if (humanCount === 0) return [];
  const quotas = Array<number>(humanCount).fill(Math.floor(encounterCount / humanCount));
  quotas[humanCount - 1] += encounterCount % humanCount;
  return quotas;
};

/**
 * Custom mode: `n * p // 100` for each human, with the remainder going to the last human whose
 * share is above 0. Returns null unless the shares total 100, because only then do the quotas
 * cover every encounter.
 */
export const customQuotas = (
  encounterCount: number,
  sharePercentages: number[]
): number[] | null => {
  if (sharePercentages.reduce((total, share) => total + share, 0) !== 100) return null;
  const quotas = sharePercentages.map((share) => Math.floor((encounterCount * share) / 100));
  const lastWithShare = sharePercentages.reduce(
    (last, share, index) => (share > 0 ? index : last),
    -1
  );
  quotas[lastWithShare] += encounterCount - quotas.reduce((total, quota) => total + quota, 0);
  return quotas;
};

export interface CustomShareRow {
  /** The share as a number, or null while the input is not a valid whole number. */
  sharePercentage: number | null;
  /** The human's encounters, or null while their share is invalid. */
  encounterCount: number | null;
}

export interface CustomShareSummary {
  rows: CustomShareRow[];
  /** The sum of the valid shares. */
  total: number;
  /** Every share is a valid whole number and they total 100. */
  isValid: boolean;
}

/**
 * The Custom table on step 6, one row per human in `rawShares` order. With every share valid
 * and a total of 100 the counts are the quotas the project is created with; otherwise each
 * valid share shows `n * p // 100`, without the remainder.
 */
export const summarizeCustomShares = (
  encounterCount: number,
  rawShares: string[]
): CustomShareSummary => {
  const shares = rawShares.map((raw) => (isValidSharePercentage(raw) ? Number(raw) : null));
  const validShares = shares.filter((share): share is number => share !== null);
  const total = validShares.reduce((sum, share) => sum + share, 0);
  const quotas =
    validShares.length === shares.length ? customQuotas(encounterCount, validShares) : null;
  return {
    rows: shares.map((sharePercentage, index) => ({
      sharePercentage,
      encounterCount:
        quotas?.[index] ??
        (sharePercentage === null ? null : Math.floor((encounterCount * sharePercentage) / 100)),
    })),
    total,
    isValid: quotas !== null,
  };
};
